import React, { Component } from "react";
import axios from "axios";
import "./Churn.css";
import config from "../../config.json";

const BASE_URL =
  (config && config.CHURNURL)
    ? config.CHURNURL
    : (window.__CHURNURL__ || "http://127.0.0.1:5000");

class ChurnApp extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeTab: "Upload",

      file: null,
      overviewData: null,
      predictions: [],
      filteredPredictions: [],
      insights: null,
      datasetPurpose: "",

      loading: false,
      message: "",
      errorMessage: "",
      backendReachable: true,

      minProb: 0,
      searchQuery: "",

      // ✅ NEW (only for visualization dropdown)
      selectedChart: "bar",
    };
  }

  componentDidMount() {
    this.fetchOverview();
  }

  /* ------------------ API ------------------ */

  fetchOverview = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/churn_overview`);
      this.setState({
        overviewData: res.data,
        datasetPurpose: res.data.purpose || "",
      });
    } catch {
      this.setState({ overviewData: null });
    }
  };

  fetchPredictions = async () => {
    const res = await axios.get(
      `${BASE_URL}/predict_churn?min_prob=${this.state.minProb}`
    );
    this.setState({
      predictions: res.data,
      filteredPredictions: res.data,
    });
  };

  fetchInsights = async () => {
    const res = await axios.get(`${BASE_URL}/customer_insights`);
    this.setState({ insights: res.data });
  };

  handleUpload = async () => {
    if (!this.state.file) return alert("Choose a CSV file");

    const formData = new FormData();
    formData.append("file", this.state.file);

    this.setState({ loading: true });

    const res = await axios.post(`${BASE_URL}/upload_churn_data`, formData);

    this.setState({
      loading: false,
      message: res.data.message,
      datasetPurpose: res.data.purpose,
      activeTab: "Predictions",
    });

    this.fetchOverview();
    this.fetchPredictions();
    this.fetchInsights();
  };

  /* ------------------ TABS ------------------ */

  renderTabs() {
    const tabs = ["Upload", "Predictions", "Dashboard", "Visualizations", "Insights"];
    return (
      <div style={styles.tabBar}>
        {tabs.map((t) => (
          <button
            key={t}
            onClick={() => this.setState({ activeTab: t })}
            style={{
              ...styles.tabButton,
              ...(this.state.activeTab === t ? styles.tabButtonActive : {}),
            }}
          >
            {t}
          </button>
        ))}
      </div>
    );
  }

  /* ------------------ UPLOAD ------------------ */

  renderUpload() {
    return (
      <div>
        <h3>Choose File & Train Model</h3>
        <input
          type="file"
          accept=".csv"
          onChange={(e) => this.setState({ file: e.target.files[0] })}
        />
        <br /><br />
        <button style={styles.primaryBtn} onClick={this.handleUpload}>
          {this.state.loading ? "Processing..." : "Upload & Train"}
        </button>
      </div>
    );
  }

  /* ------------------ DASHBOARD ------------------ */

  renderDashboard() {
    const d = this.state.overviewData;
    if (!d) return <p>Upload a dataset to see dashboard</p>;

    return (
      <div>
        <h3>Churn Overview</h3>
        <div style={styles.cardGrid}>
          <div style={{ ...styles.card, borderTop: "4px solid #1976d2" }}>
            <div style={{ color: "#1976d2", fontWeight: "bold" }}>Total Records</div>
            <div style={{ color: "#1976d2", fontSize: 22, fontWeight: "bold" }}>
              {d.total_customers}
            </div>
          </div>

          <div style={{ ...styles.card, borderTop: "4px solid #e53935" }}>
            <div style={{ color: "#e53935", fontWeight: "bold" }}>Total Churned</div>
            <div style={{ color: "#e53935", fontSize: 22, fontWeight: "bold" }}>
              {d.churned}
            </div>
          </div>

          <div style={{ ...styles.card, borderTop: "4px solid #43a047" }}>
            <div style={{ color: "#43a047", fontWeight: "bold" }}>Total Retained</div>
            <div style={{ color: "#43a047", fontSize: 22, fontWeight: "bold" }}>
              {d.retained}
            </div>
          </div>

          <div style={{ ...styles.card, borderTop: "4px solid #e53935" }}>
            <div style={{ color: "#e53935", fontWeight: "bold" }}>Churn Rate</div>
            <div style={{ color: "#e53935", fontSize: 22, fontWeight: "bold" }}>
              {d.churn_rate}%
            </div>
          </div>

          <div style={{ ...styles.card, borderTop: "4px solid #43a047" }}>
            <div style={{ color: "#43a047", fontWeight: "bold" }}>Retention Rate</div>
            <div style={{ color: "#43a047", fontSize: 22, fontWeight: "bold" }}>
              {d.retention_rate}%
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ------------------ VISUALIZATIONS (ONLY CHANGE) ------------------ */

  renderVisualizations() {
    const { selectedChart } = this.state;

    return (
      <div>
        <h3>Churn Visualizations</h3>

        {/* ✅ Dropdown (NEW) */}
        <div style={{ marginBottom: 12 }}>
          <label><strong>Select Chart:</strong></label><br />
          <select
            value={selectedChart}
            onChange={(e) => this.setState({ selectedChart: e.target.value })}
          >
            <option value="bar">Bar Chart</option>
            <option value="pie">Pie Chart</option>
          </select>
        </div>

        <button
          style={styles.outlineBtn}
          onClick={() => window.open(`${BASE_URL}/download_excel`)}
        >
          ⬇ Download Excel
        </button>

        <div style={styles.chartRow}>
          {selectedChart === "bar" && (
            <img
              src={`${BASE_URL}/churn_bar_chart`}
              alt="Bar"
              style={styles.chartImg}
            />
          )}

          {selectedChart === "pie" && (
            <img
              src={`${BASE_URL}/churn_pie_chart`}
              alt="Pie"
              style={styles.chartImg}
            />
          )}
        </div>
      </div>
    );
  }

  /* ------------------ PREDICTIONS ------------------ */

  renderPredictions() {
    const { predictions, datasetPurpose, minProb, searchQuery } = this.state;

    if (!predictions || predictions.length === 0) {
      return <p>No predictions available. Upload a dataset first.</p>;
    }

    return (
      <div>
        {datasetPurpose && (
          <div style={styles.infoBox}>
            <strong>Dataset Purpose</strong>
            <div style={{ marginTop: 6 }}>{datasetPurpose}</div>
          </div>
        )}

        <div style={styles.predictionHeader}>
          <div style={styles.filterGroup}>
            <div>
              <label>Min Probability</label><br />
              <input
                type="number"
                step="0.01"
                value={minProb}
                onChange={(e) =>
                  this.setState({ minProb: e.target.value }, this.fetchPredictions)
                }
              />
            </div>

            <div>
              <label>Search</label><br />
              <input
                type="text"
                placeholder="Search any value..."
                value={searchQuery}
                onChange={(e) => {
                  const q = e.target.value.toLowerCase();
                  this.setState({
                    searchQuery: q,
                    filteredPredictions: predictions.filter((r) =>
                      Object.values(r).join(" ").toLowerCase().includes(q)
                    ),
                  });
                }}
              />
            </div>
          </div>

          <div>
            <button
              style={styles.outlineBtn}
              onClick={() => window.open(`${BASE_URL}/download_predictions`)}
            >
              ⬇ Download Doc
            </button>
            <button
              style={{ ...styles.outlineBtn, marginLeft: 8 }}
              onClick={() => window.open(`${BASE_URL}/download_excel`)}
            >
              ⬇ Download Excel
            </button>
          </div>
        </div>

        <table style={styles.table}>
          <thead>
            <tr>
              {Object.keys(predictions[0]).map((k) => (
                <th key={k}>{k}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {this.state.filteredPredictions.map((row, i) => (
              <tr key={i}>
                {Object.values(row).map((v, j) => (
                  <td key={j}>{String(v)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  /* ------------------ INSIGHTS ------------------ */

  renderInsights() {
    const insights = this.state.insights;
    if (!insights) return <p>No insights yet</p>;

    return (
      <table style={styles.table}>
        <thead>
          <tr>
            <th>Column</th>
            <th>Count</th>
            <th>Mean</th>
            <th>Min</th>
            <th>Max</th>
          </tr>
        </thead>
        <tbody>
          {Object.keys(insights).map((k) => (
            <tr key={k}>
              <td>{k}</td>
              <td>{insights[k].count}</td>
              <td>{insights[k].mean}</td>
              <td>{insights[k].min}</td>
              <td>{insights[k].max}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  render() {
    return (
      <div style={styles.app}>
        <h1 style={styles.title}>📊 SmartAI Churn Insights</h1>
        {this.renderTabs()}
        <div style={styles.panel}>
          {this.state.activeTab === "Upload" && this.renderUpload()}
          {this.state.activeTab === "Predictions" && this.renderPredictions()}
          {this.state.activeTab === "Dashboard" && this.renderDashboard()}
          {this.state.activeTab === "Visualizations" && this.renderVisualizations()}
          {this.state.activeTab === "Insights" && this.renderInsights()}
        </div>
      </div>
    );
  }
}

/* ------------------ STYLES ------------------ */

const styles = {
  app: { padding: 20 },
  title: { textAlign: "center", color: "#1976d2" },

  tabBar: { display: "flex", marginBottom: 20 },
  tabButton: { flex: 1, padding: 12, border: "none" },
  tabButtonActive: { background: "#1976d2", color: "#fff" },

  panel: { padding: 20, background: "#fff" },
  primaryBtn: { padding: "8px 16px", background: "#1976d2", color: "#fff" },

  outlineBtn: { padding: "8px 16px" },

  cardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))",
    gap: 12,
  },
  card: { padding: 16, boxShadow: "0 1px 4px rgba(0,0,0,.1)" },

  chartRow: { display: "flex", gap: 20, marginTop: 20 },
  chartImg: { maxWidth: 400, border: "1px solid #ccc" },

  table: { width: "100%", borderCollapse: "collapse" },

  infoBox: {
    padding: 12,
    marginBottom: 12,
    background: "#f1f5f9",
    borderLeft: "4px solid #1976d2",
    borderRadius: 4,
  },

  predictionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginBottom: 12,
  },

  filterGroup: {
    display: "flex",
    gap: 16,
  },
};

export default ChurnApp;
